import { useEffect, useState } from 'react'
import { nf } from '@/shared/util/numbers'
import { InfoTip } from '@/shared/ui/InfoTip'
import { useAuth } from '@/features/auth/AuthProvider'
import { loadProCompanies } from './repository/proCompaniesRepository'

/**
 * Skida PRO members on the app dashboard — 26 Oct 2026.
 *
 * Premium comes with a Skida PRO seat, so a guide in a paying company is an
 * app user who pays, through the company rather than the App Store. They are
 * kept out of the app's own trial figures and counted here instead, from the
 * same product records the PRO dashboard reads.
 */
export function ProSubscribersCard() {
  const { productEmail } = useAuth()
  const [counts, setCounts] = useState<{ paid: number; granted: number; members: number } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    setFailed(false)
    loadProCompanies()
      .then((companies) => {
        const access = new Map<string, string>()
        // One guide in two companies is one person; the better access wins.
        const rank: Record<string, number> = { paid: 3, granted: 2, expired: 1, registered: 0 }
        for (const m of companies.flatMap((c) => c.members)) {
          const prev = access.get(m.uid)
          if (!prev || rank[m.access]! > rank[prev]!) access.set(m.uid, m.access)
        }
        const all = [...access.values()]
        if (live)
          setCounts({
            paid: all.filter((a) => a === 'paid').length,
            granted: all.filter((a) => a === 'granted').length,
            members: all.length,
          })
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [productEmail])

  return (
    <div className="dash__kpis">
      <div className="dash__kpi">
        <div className="dash__kpilabel">
          PRO subscribers
          <InfoTip label="PRO subscribers">
            Guides with Premium through a paid Skida PRO seat — app users who pay, through their
            company rather than the App Store. Skida PRO members are kept out of the app trial and
            the trial emails, because Premium comes with PRO.
          </InfoTip>
        </div>
        <div className="dash__kpivalue">{failed ? '—' : counts ? nf(counts.paid) : '…'}</div>
        <div className="dash__kpisub">
          {counts
            ? `${nf(counts.granted)} more on a PRO grant · ${nf(counts.members)} PRO members in all`
            : failed
              ? 'Could not read Skida PRO'
              : 'paid Skida PRO seats'}
        </div>
      </div>
    </div>
  )
}
